import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import HTMLDisplay from "../../../utils/HTMLDisplay";
import truncate from "truncate-html";
import axios from "axios";
import "../../../assets/css/PublicAnnounceSection.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const hasImage = (value) =>
  typeof value === "string" &&
  value.trim() !== "" &&
  value !== "undefined" &&
  value !== "null";

const truncateTitle = (html, length = 12) =>
  truncate(html || "", length, { byWords: true });

function PublicAnnounceSection({
  datacontent = [],
  initialDownloadData = [],
  initialPagination,
  id,
}) {
  const [newsList, setNewsList] = useState(datacontent || []);
  const [newsDownloadList, setNewsDownloadList] = useState(
    initialDownloadData || [],
  );

  const [page, setPage] = useState(1);
  const [pageDownload, setPageDownload] = useState(1);

  const [pagination, setPagination] = useState(
    initialPagination || { current_page: 1, last_page: 1 },
  );

  const [paginationDownload, setPaginationDownload] = useState({
    current_page: 1,
    last_page: 1,
  });

  const [loadingNews, setLoadingNews] = useState(false);
  const [loadingDownloads, setLoadingDownloads] = useState(false);
  const [newsError, setNewsError] = useState(false);
  const [downloadError, setDownloadError] = useState(false);

  const headerTitle = datacontent?.[0]?.title || "Public Announcements";

  const hasDownloads =
    datacontent?.[0]?.aligment009?.toLowerCase() === "center";

  useEffect(() => {
    setNewsList(datacontent || []);
  }, [datacontent]);

  useEffect(() => {
    setNewsDownloadList(initialDownloadData || []);
  }, [initialDownloadData]);

  const fetchAnnouncements = useCallback(async () => {
    setLoadingNews(true);
    setNewsError(false);

    try {
      const response = await axios.get(
        `${BASE_URL}posdata?pageName[eq]=${id}&columes[eq]=title_show_echo_01&ministry[eq]=1&status02[eq]=publish&status[eq]=Public&visibility[eq]=Public&display01[eq]=1&page=${page}&per_page=1`,
      );

      setNewsList(response?.data?.allData || []);

      setPagination(
        response?.data?.pagination || {
          current_page: page,
          last_page: 1,
        },
      );
    } catch (error) {
      console.error("Unable to load public announcements:", error);
      setNewsError(true);
    } finally {
      setLoadingNews(false);
    }
  }, [id, page]);

  const fetchDownloads = useCallback(async () => {
    setLoadingDownloads(true);
    setDownloadError(false);

    try {
      const response = await axios.get(
        `${BASE_URL}posdata?columes[eq]=title_show_echo_01wa&ministry[eq]=1&status02[eq]=publish&status[eq]=Public&visibility[eq]=Public&page=${pageDownload}&per_page=5`,
      );

      setNewsDownloadList(response?.data?.allData || []);

      setPaginationDownload(
        response?.data?.pagination || {
          current_page: pageDownload,
          last_page: 1,
        },
      );
    } catch (error) {
      console.error("Unable to load downloadable resources:", error);
      setDownloadError(true);
    } finally {
      setLoadingDownloads(false);
    }
  }, [pageDownload]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  useEffect(() => {
    fetchDownloads();
  }, [fetchDownloads]);

  const renderPagination = (currentPage, lastPage, onPageChange, disabled) => (
    <nav className="pa-pagination" aria-label="Pagination">
      <span className="pa-page-label">
        Page <strong>{currentPage}</strong> of <strong>{lastPage}</strong>
      </span>

      <div className="pa-page-buttons">
        <button
          type="button"
          onClick={() => onPageChange(1)}
          disabled={disabled || currentPage <= 1}
          aria-label="First page"
        >
          <i className="fa-solid fa-angles-left" />
        </button>

        <button
          type="button"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={disabled || currentPage <= 1}
          aria-label="Previous page"
        >
          <i className="fa-solid fa-angle-left" />
        </button>

        <span className="pa-current-page">{currentPage}</span>

        <button
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={disabled || currentPage >= lastPage}
          aria-label="Next page"
        >
          <i className="fa-solid fa-angle-right" />
        </button>

        <button
          type="button"
          onClick={() => onPageChange(lastPage)}
          disabled={disabled || currentPage >= lastPage}
          aria-label="Last page"
        >
          <i className="fa-solid fa-angles-right" />
        </button>
      </div>
    </nav>
  );

  const renderAnnouncement = (item, index) => {
    const alignment = item?.aligment009?.toLowerCase();
    const isLeft = alignment === "left";
    const isRight = alignment === "right";
    const isCenter = alignment === "center";

    const imageUrl = hasImage(item.passport)
      ? `${BACKEND_URL}upload/${item.passport}`
      : null;

    const showReadMore = item.textSize === "0px";

    return (
      <article
        key={item.id || index}
        className={`pa-announcement pa-announcement-${alignment || "left"}`}
      >
        {imageUrl && (
          <div className="pa-announcement-image">
            <img
              src={imageUrl}
              alt={item.title || "Announcement"}
              loading="lazy"
              decoding="async"
            />
          </div>
        )}

        <div className="pa-announcement-content">
          <span className="pa-announcement-label">
            <i className="fa-solid fa-bullhorn" aria-hidden="true" />
            Public Announcement
          </span>

          <div className="pa-announcement-title">
            <HTMLDisplay html={item.story01 || ""} type="title" />
          </div>

          <div className="pa-announcement-body">
            <HTMLDisplay html={item.story01 || ""} type="body" />
          </div>

          {showReadMore && (
            <Link to={`/read/${item.id}`} className="pa-read-more">
              Read Full Announcement
              <i className="fa-solid fa-arrow-right" aria-hidden="true" />
            </Link>
          )}
        </div>
      </article>
    );
  };

  return (
    <section id="portfolio-details" className="pa-section section">
      <div className="container">
        {/* Section heading */}
        <div className="tm-news-heading">
          <div className="tm-news-heading-copy">
            <span className="tm-news-eyebrow">OFFICIAL INFORMATION</span>

            <h2>{headerTitle}</h2>
            {/* 
            <p>
              Discover the latest news, activities and developments
              from the Federal Ministry of Housing and Urban Development.
            </p> */}
          </div>
          <div className="tm-modern-heading-mark" aria-hidden="true">
            <i className="fa-solid fa-bullhorn" />
          </div>
        </div>

        <div className={`pa-layout ${hasDownloads ? "pa-with-downloads" : ""}`}>
          {/* Announcements */}
          <div className="pa-announcements-column">
            {loadingNews && (
              <div className="pa-status" role="status">
                Loading announcements...
              </div>
            )}

            {newsError && (
              <div className="pa-status pa-error" role="alert">
                Unable to load announcements.
                <button type="button" onClick={fetchAnnouncements}>
                  Try Again
                </button>
              </div>
            )}

            {!loadingNews && !newsError && newsList.length === 0 && (
              <div className="pa-status">
                No announcements are available at the moment.
              </div>
            )}

            {newsList.map(renderAnnouncement)}

            {Number(pagination?.last_page || 1) > 1 &&
              renderPagination(
                Number(pagination?.current_page || page),
                Number(pagination?.last_page || 1),
                setPage,
                loadingNews,
              )}
          </div>

          {/* Downloadable resources */}
          {hasDownloads && (
            <aside className="pa-downloads">
              <div className="pa-downloads-heading">
                <span className="pa-downloads-icon">
                  <i className="fa-solid fa-folder-open" aria-hidden="true" />
                </span>
                <div>
                  <span className="pa-downloads-eyebrow">DOCUMENT CENTRE</span>
                  <h3>Downloadable Resources</h3>
                </div>
              </div>

              <p className="pa-downloads-intro">
                Access official documents, notices and publications.
              </p>

              {loadingDownloads && (
                <div className="pa-download-status" role="status">
                  Loading documents...
                </div>
              )}

              {downloadError && (
                <div className="pa-download-status" role="alert">
                  Unable to load documents.
                  <button type="button" onClick={fetchDownloads}>
                    Retry
                  </button>
                </div>
              )}

              {!loadingDownloads &&
                !downloadError &&
                newsDownloadList.length === 0 && (
                  <p className="pa-download-status">
                    No documents are currently available.
                  </p>
                )}

              <div className="pa-document-list">
                {newsDownloadList.map((file, index) => {
                  const fileUrl = hasImage(file.addressLink)
                    ? `${BACKEND_URL}upload/${file.addressLink}`
                    : null;

                  if (!fileUrl) return null;

                  return (
                    <a
                      key={file.id || index}
                      href={fileUrl}
                      download
                      className="pa-document-item"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <span className="pa-document-icon">
                        <i
                          className="fa-regular fa-file-pdf"
                          aria-hidden="true"
                        />
                      </span>

                      <span className="pa-document-name">
                        {truncateTitle(file.title, 12)}
                        <small>Download document</small>
                      </span>

                      <i
                        className="fa-solid fa-download pa-document-download"
                        aria-hidden="true"
                      />
                    </a>
                  );
                })}
              </div>

              {Number(paginationDownload?.last_page || 1) > 1 &&
                renderPagination(
                  Number(paginationDownload?.current_page || pageDownload),
                  Number(paginationDownload?.last_page || 1),
                  setPageDownload,
                  loadingDownloads,
                )}
            </aside>
          )}
        </div>
      </div>
    </section>
  );
}

export default React.memo(PublicAnnounceSection);
